<template>
  <div class="third-party-log">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t("__thirdPartyLogManagement") }}</h2>

      <!-- 第一層篩選 -->
      <div class="card mb-3">
        <div class="card-body">
          <div class="row g-2 align-items-end">
            <!-- 平台 -->
            <div class="col-md-2 col-6 mb-2">
              <label class="form-label small text-muted mb-1">{{ $t("__tplPlatform") }}</label>
              <select v-model="filters.platform" class="form-select" @change="searchLogs">
                <option :value="null">{{ $t("__tplAllPlatforms") }}</option>
                <option
                  v-for="opt in platformOptions"
                  :key="opt.value"
                  :value="opt.value"
                >{{ opt.label }}</option>
              </select>
            </div>

            <!-- 資料類型 -->
            <div class="col-md-2 col-6 mb-2">
              <label class="form-label small text-muted mb-1">{{ $t("__tplDataType") }}</label>
              <select v-model="filters.data_type" class="form-select" @change="searchLogs">
                <option :value="null">{{ $t("__tplAllDataTypes") }}</option>
                <option
                  v-for="opt in dataTypeOptions"
                  :key="opt.value"
                  :value="opt.value"
                >{{ opt.label }}</option>
              </select>
            </div>

            <!-- 狀態 -->
            <div class="col-md-2 col-6 mb-2">
              <label class="form-label small text-muted mb-1">{{ $t("__status") }}</label>
              <select v-model="filters.status" class="form-select" @change="searchLogs">
                <option :value="null">{{ $t("__tplAllStatuses") }}</option>
                <option
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  :value="opt.value"
                >{{ opt.label }}</option>
              </select>
            </div>

            <!-- 日期區間 -->
            <div class="col-md-3 col-6 mb-2">
              <label class="form-label small text-muted mb-1">{{ $t("__tplDateRange") }}</label>
              <div class="d-flex align-items-center">
                <input
                  type="date"
                  v-model="filters.date_from"
                  class="form-control"
                  @change="searchLogs"
                />
                <span class="mx-1">{{ $t("__to") }}</span>
                <input
                  type="date"
                  v-model="filters.date_to"
                  class="form-control"
                  @change="searchLogs"
                />
              </div>
            </div>

            <!-- 住民關鍵字 -->
            <div class="col-md-3 col-12 mb-2">
              <label class="form-label small text-muted mb-1">{{ $t("__tplResidentKeyword") }}</label>
              <form @submit.prevent="searchLogs">
                <div class="input-group rounded-pill">
                  <input
                    v-model="filters.keyword"
                    type="text"
                    class="form-control rounded-pill"
                    :placeholder="$t('__tplResidentKeywordPlaceholder')"
                  />
                  <button type="submit" class="btn btn-link px-2 text-dark">
                    <i class="bi bi-search"></i>
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div class="d-flex align-items-center justify-content-between flex-wrap mt-2">
            <!-- 排除噪音 -->
            <div class="form-check mb-2">
              <input
                id="tpl-exclude-noise"
                type="checkbox"
                class="form-check-input"
                v-model="filters.exclude_noise"
                @change="searchLogs"
              />
              <label class="form-check-label" for="tpl-exclude-noise">
                {{ $t("__tplExcludeNoise") }}
                <i
                  class="bi bi-question-circle text-muted ms-1"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__tplExcludeNoiseTip')"
                ></i>
              </label>
            </div>

            <div class="mb-2">
              <button
                type="button"
                class="btn btn-outline-primary px-3 rounded-pill"
                :disabled="isLoading"
                @click="fetchLogs"
              >
                <i class="bi bi-arrow-clockwise"></i>
                {{ $t("__refresh") }}
              </button>
              <button
                type="button"
                class="btn btn-link text-decoration-none px-2 ms-2"
                @click="showAdvanced = !showAdvanced"
              >
                {{ $t("__tplAdvancedFilter") }}
                <i class="bi" :class="showAdvanced ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </button>
              <button
                v-if="hasActiveFilters"
                type="button"
                class="btn btn-primary px-3 rounded-pill ms-2"
                @click="clearFilters"
              >
                {{ $t("__clearFilters") }}
              </button>
            </div>
          </div>

          <!-- 進階篩選（收合） -->
          <div v-show="showAdvanced" class="border-top pt-3 mt-2">
            <div class="row g-2">
              <div class="col-md-3 col-6 mb-2">
                <label class="form-label small text-muted mb-1">{{ $t("__tplAgency") }}</label>
                <v-select
                  v-model="filters.agency_id"
                  :options="allAgencies"
                  label="name"
                  :reduce="agency => agency.id"
                  :placeholder="$t('__searchAgencyName')"
                  class="style-chooser"
                  :components="{Deselect, OpenIndicator}"
                  :get-option-label="option => `${option.codename} / ${option.name}`"
                >
                </v-select>
              </div>
              <div class="col-md-3 col-6 mb-2">
                <label class="form-label small text-muted mb-1">{{ $t("__tplBatchId") }}</label>
                <input v-model="filters.batch_id" type="text" class="form-control" />
              </div>
              <div class="col-md-3 col-6 mb-2">
                <label class="form-label small text-muted mb-1">{{ $t("__tplErrorCode") }}</label>
                <input v-model="filters.error_code" type="text" class="form-control" />
              </div>
              <div class="col-md-3 col-6 mb-2">
                <label class="form-label small text-muted mb-1">{{ $t("__tplJobUuid") }}</label>
                <input v-model="filters.job_uuid" type="text" class="form-control" />
              </div>
              <div class="col-md-3 col-6 mb-2">
                <label class="form-label small text-muted mb-1">{{ $t("__tplDeviceId") }}</label>
                <input v-model="filters.device_id" type="text" class="form-control" />
              </div>
              <div class="col-md-3 col-6 mb-2">
                <label class="form-label small text-muted mb-1">{{ $t("__tplLogKind") }}</label>
                <select v-model="filters.log_kind" class="form-select">
                  <option value="all">{{ $t("__tplLogKindAll") }}</option>
                  <option value="original">{{ $t("__tplLogKindOriginal") }}</option>
                  <option value="resend">{{ $t("__tplLogKindResend") }}</option>
                </select>
              </div>
              <div class="col-md-6 col-12 mb-2">
                <label class="form-label small text-muted mb-1">{{ $t("__tplMessageKeyword") }}</label>
                <input v-model="filters.message" type="text" class="form-control" />
              </div>
            </div>
            <div class="text-end">
              <button type="button" class="btn btn-primary rounded-pill px-4" @click="searchLogs">
                {{ $t("__tplApplyFilter") }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 資料表格 -->
      <div class="card">
        <div class="card-body">
          <b-table
            :responsive="$store.state.windowWidth > $store.state.breakpoints.sm"
            :stacked="$store.state.windowWidth <= $store.state.breakpoints.sm"
            striped
            borderless
            head-variant="light"
            :items="logs || []"
            :fields="fields"
            :busy="isLoading"
            show-empty
            :empty-text="$t('__noData')"
          >
            <template v-slot:cell(sent_at)="data">
              {{ formatDateTime(data.item.sent_at) }}
            </template>

            <template v-slot:cell(resident_name)="data">
              <div>
                <span>{{ data.item.resident_name || "-" }}</span>
                <template v-if="data.item.is_resend">
                  <br />
                  <span class="badge bg-info">{{ $t("__tplResend") }}</span>
                </template>
              </div>
            </template>

            <template v-slot:cell(agency_name)="data">
              {{ data.item.agency_name || "-" }}
            </template>

            <template v-slot:cell(status)="data">
              <span class="badge" :class="'bg-' + getStatusVariant(data.item.status)">
                {{ data.item.status_label || data.item.status }}
              </span>
            </template>

            <template v-slot:cell(error_summary)="data">
              <div v-if="data.item.error_code || data.item.error_summary" style="max-width: 260px;">
                <small v-if="data.item.error_code_label" class="d-block text-danger">
                  {{ data.item.error_code_label }}
                </small>
                <small
                  v-if="data.item.error_summary"
                  class="d-block text-muted text-truncate"
                  :title="data.item.error_summary"
                >{{ data.item.error_summary }}</small>
              </div>
              <span v-else class="text-muted">-</span>
            </template>

            <template v-slot:cell(actions)="data">
              <button
                @click="showDetail(data.item)"
                type="button"
                class="btn btn-link px-1 text-dark"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                :title="$t('__viewDetail')"
              >
                <i class="bi bi-eye mx-1 fs-5"></i>
              </button>
            </template>
          </b-table>

          <div v-if="isLoading" class="text-center my-4">
            <b-spinner variant="primary"></b-spinner>
          </div>
        </div>
      </div>

      <!-- 分頁 + 每頁筆數 -->
      <div class="row align-items-center mt-3" v-if="totalRows">
        <div class="col-sm-auto col-12 mb-2 mb-sm-0">
          <div class="d-flex align-items-center">
            <span class="me-2 text-muted small">{{ $t("__tplPerPage") }}</span>
            <select v-model.number="perPage" class="form-select form-select-sm w-auto" @change="onPerPageChange">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>
        </div>
        <div class="col-sm col-12 text-right text-sm-left">
          {{
            $t("__paginationInfo", {
              startNub: (currentPage - 1) * perPage + 1,
              entNub: Math.min(currentPage * perPage, totalRows),
              sum: totalRows,
            })
          }}
        </div>
        <div class="col-sm col-12">
          <b-pagination
            v-if="totalRows > perPage"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="right"
            first-number
            last-number
            class="mb-sm-0 mb-3 mt-sm-0 mt-3"
            @change="onPageChange"
          ></b-pagination>
        </div>
      </div>
    </div>

    <!-- 詳情 Modal -->
    <div class="modal fade" id="tpl-detail-modal" tabindex="-1" aria-hidden="true" ref="detailModal">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t("__tplLogDetail") }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="detail">
            <!-- 狀態區 -->
            <div class="mb-3">
              <span class="badge fs-6" :class="'bg-' + getStatusVariant(detail.status)">
                {{ detail.status_label || detail.status }}
              </span>
              <span v-if="detail.error_code_label" class="ms-2 text-danger fw-bold">
                {{ detail.error_code_label }}
              </span>
              <div v-if="detail.error_summary" class="text-muted mt-1">
                {{ detail.error_summary }}
              </div>
            </div>

            <!-- 基本資訊 -->
            <div class="table-responsive">
              <table class="table table-sm mb-3">
                <tbody>
                  <tr>
                    <th class="text-muted" style="width: 40%;">{{ $t("__tplMeasurementTime") }}</th>
                    <td>
                      <div>{{ formatDateTime(detail.measurement_time) || "-" }}</div>
                      <small v-if="detail.measurement_time_utc" class="text-muted">
                        UTC: {{ detail.measurement_time_utc }}
                      </small>
                    </td>
                  </tr>
                  <tr>
                    <th class="text-muted">{{ $t("__tplSentAt") }}</th>
                    <td>{{ formatDateTime(detail.sent_at) || "-" }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">{{ $t("__createdAt") }}</th>
                    <td>{{ formatDateTime(detail.created_at) || "-" }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">{{ $t("__tplPlatform") }}</th>
                    <td>{{ detail.platform_label || detail.platform }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">{{ $t("__tplDataType") }}</th>
                    <td>{{ detail.data_type_label || detail.data_type }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">{{ $t("__tplResident") }}</th>
                    <td>{{ detail.resident_name || "-" }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">{{ $t("__tplAgency") }}</th>
                    <td>{{ detail.agency_name || "-" }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">{{ $t("__tplBatchId") }}</th>
                    <td>{{ detail.batch_id || "-" }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">{{ $t("__tplJobUuid") }}</th>
                    <td>{{ detail.job_uuid || "-" }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">{{ $t("__tplDeviceId") }}</th>
                    <td>{{ detail.device_id || "-" }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">{{ $t("__tplIsResend") }}</th>
                    <td>
                      <span v-if="detail.is_resend" class="badge bg-info">{{ $t("__tplYes") }}</span>
                      <span v-else class="text-muted">{{ $t("__tplNo") }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- CaringCM 明細 -->
            <div v-if="detail.details && detail.details.length" class="mb-3">
              <h6 class="fw-bold">{{ $t("__tplCaringCmDetails") }}</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th v-for="col in detailColumns" :key="col" class="small">{{ col }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, idx) in detail.details" :key="idx">
                      <td v-for="col in detailColumns" :key="col" class="small">
                        {{ formatCell(row[col]) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Request Body -->
            <div class="mb-2" v-if="detail.request_body">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="showRequest = !showRequest"
              >
                {{ $t("__tplRequestBody") }}
                <i class="bi" :class="showRequest ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </button>
              <pre v-show="showRequest" class="mt-2 mb-0">{{ prettyJson(detail.request_body) }}</pre>
            </div>

            <!-- Response Body -->
            <div class="mb-2" v-if="detail.response_body">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="showResponse = !showResponse"
              >
                {{ $t("__tplResponseBody") }}
                <i class="bi" :class="showResponse ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </button>
              <pre v-show="showResponse" class="mt-2 mb-0">{{ prettyJson(detail.response_body) }}</pre>
            </div>

            <!-- error_history -->
            <div class="mt-3" v-if="detail.error_history && detail.error_history.length">
              <h6 class="fw-bold">{{ $t("__tplErrorHistory") }}</h6>
              <ul class="list-group">
                <li
                  v-for="(item, idx) in detail.error_history"
                  :key="idx"
                  class="list-group-item"
                >
                  <div class="d-flex justify-content-between">
                    <small class="text-muted">{{ formatDateTime(item.at || item.time || item.created_at) }}</small>
                    <small v-if="item.error_code" class="text-danger">{{ item.error_code }}</small>
                  </div>
                  <div>{{ item.message || item.summary || formatCell(item) }}</div>
                </li>
              </ul>
            </div>
          </div>
          <div class="modal-body text-center py-5" v-else>
            <b-spinner variant="primary"></b-spinner>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Tooltip, Modal } from "bootstrap";
import i18n from "@/lang/lang.js";
import { thirdPartyLog } from "@/http/api/thirdPartyLog.js";
import { agency } from "@/http/api/agency.js";
import { mapState, mapMutations } from "vuex";
import moment from "moment-timezone";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "ThirdPartyLog",
  components: { vSelect },
  data() {
    return {
      Deselect: {
        render: createElement => createElement('span', ''),
      },
      OpenIndicator: {
        render: createElement => createElement('span', [
          createElement('img', {
            attrs: {
              src: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e"
            },
            style: { width: '16px', height: '16px' }
          })
        ]),
      },
      logs: null,
      allAgencies: [],
      platformOptions: [],
      dataTypeOptions: [],
      statusOptions: [],
      filters: {
        platform: null,
        data_type: null,
        status: null,
        date_from: null,
        date_to: null,
        keyword: null,
        exclude_noise: true,
        agency_id: null,
        batch_id: null,
        error_code: null,
        job_uuid: null,
        device_id: null,
        log_kind: "all",
        message: null,
      },
      showAdvanced: false,
      showRequest: false,
      showResponse: false,
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      isLoading: false,
      detail: null,
      detailModal: null,
      bootstrap_tooltip: null,
    };
  },
  computed: {
    ...mapState(["token", "userInfo", "isSystemManage"]),
    fields() {
      return [
        {
          key: "sent_at",
          label: i18n.t("__tplTime"),
          class: "align-middle",
        },
        {
          key: "platform_label",
          label: i18n.t("__tplPlatform"),
          class: "align-middle",
        },
        {
          key: "data_type_label",
          label: i18n.t("__tplDataType"),
          class: "align-middle",
        },
        {
          key: "resident_name",
          label: i18n.t("__tplResident"),
          class: "align-middle",
        },
        {
          key: "agency_name",
          label: i18n.t("__tplAgency"),
          class: "align-middle",
        },
        {
          key: "status",
          label: i18n.t("__status"),
          class: "align-middle text-center",
        },
        {
          key: "error_summary",
          label: i18n.t("__tplErrorSummary"),
          class: "align-middle",
        },
        {
          key: "actions",
          label: i18n.t("__actions"),
          class: "align-middle text-center",
          thStyle: { width: "90px" },
        },
      ];
    },
    hasActiveFilters() {
      const f = this.filters;
      return !!(
        f.platform ||
        f.data_type ||
        f.status ||
        f.keyword ||
        f.agency_id ||
        f.batch_id ||
        f.error_code ||
        f.job_uuid ||
        f.device_id ||
        f.message ||
        (f.log_kind && f.log_kind !== "all") ||
        !f.exclude_noise
      );
    },
    // CaringCM 明細動態欄位 = 所有元素 keys 的聯集
    detailColumns() {
      if (!this.detail || !this.detail.details || !this.detail.details.length) {
        return [];
      }
      const cols = [];
      this.detail.details.forEach((row) => {
        if (row && typeof row === "object") {
          Object.keys(row).forEach((k) => {
            if (cols.indexOf(k) === -1) {
              cols.push(k);
            }
          });
        }
      });
      return cols;
    },
  },
  methods: {
    ...mapMutations(["Loading", "Loaded", "AlertsInfo", "Alerted"]),

    buildParams() {
      const f = this.filters;
      return {
        page: this.currentPage,
        per_page: this.perPage,
        platform: f.platform || undefined,
        data_type: f.data_type || undefined,
        status: f.status || undefined,
        date_from: f.date_from || undefined,
        date_to: f.date_to || undefined,
        keyword: f.keyword || undefined,
        exclude_noise: f.exclude_noise ? 1 : 0,
        agency_id: f.agency_id || undefined,
        batch_id: f.batch_id || undefined,
        error_code: f.error_code || undefined,
        job_uuid: f.job_uuid || undefined,
        device_id: f.device_id || undefined,
        log_kind: f.log_kind || undefined,
        message: f.message || undefined,
      };
    },

    async fetchLogs() {
      this.isLoading = true;
      this.Loading();
      try {
        const response = await thirdPartyLog.get(this.buildParams(), this.token);
        if (response.status <= 201 && response.data.status === "success") {
          this.$set(this, "logs", response.data.data.data || []);
          this.totalRows = response.data.data.total || 0;
          this.currentPage = response.data.data.current_page || 1;
        } else {
          this.AlertsInfo({
            state: "error",
            title: i18n.t("__error"),
            info: (response.data && response.data.errMsg) || i18n.t("__fetchError"),
          });
          this.Alerted();
        }
      } catch (error) {
        this.AlertsInfo({
          state: "error",
          title: i18n.t("__error"),
          info: error.message,
        });
        this.Alerted();
      } finally {
        this.isLoading = false;
        this.Loaded();
      }
    },

    async fetchOptions() {
      try {
        const [platforms, dataTypes, statuses] = await Promise.all([
          thirdPartyLog.getPlatforms(this.token),
          thirdPartyLog.getDataTypes(this.token),
          thirdPartyLog.getStatuses(this.token),
        ]);
        if (platforms.status <= 201 && platforms.data.status === "success") {
          this.platformOptions = platforms.data.data || [];
        }
        if (dataTypes.status <= 201 && dataTypes.data.status === "success") {
          this.dataTypeOptions = dataTypes.data.data || [];
        }
        if (statuses.status <= 201 && statuses.data.status === "success") {
          this.statusOptions = statuses.data.data || [];
        }
      } catch (error) {
        console.error("Failed to fetch options:", error);
      }
    },

    getAgencies() {
      agency.get(null, this.token).then((res) => {
        if (res && res.data && res.data.status === "success") {
          this.allAgencies = res.data.data;
        }
      });
    },

    searchLogs() {
      this.currentPage = 1;
      this.fetchLogs();
    },

    onPageChange(page) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.fetchLogs();
      }
    },

    onPerPageChange() {
      this.currentPage = 1;
      this.fetchLogs();
    },

    clearFilters() {
      this.filters = {
        platform: null,
        data_type: null,
        status: null,
        date_from: null,
        date_to: null,
        keyword: null,
        exclude_noise: true,
        agency_id: null,
        batch_id: null,
        error_code: null,
        job_uuid: null,
        device_id: null,
        log_kind: "all",
        message: null,
      };
      this.setDefaultDateRange();
      this.currentPage = 1;
      this.fetchLogs();
    },

    async showDetail(row) {
      this.detail = null;
      this.showRequest = false;
      this.showResponse = false;
      if (!this.detailModal) {
        this.detailModal = new Modal(this.$refs.detailModal);
      }
      this.detailModal.show();
      try {
        const response = await thirdPartyLog.getDetail(row.id, this.token);
        if (response.status <= 201 && response.data.status === "success") {
          this.detail = response.data.data;
        } else {
          this.AlertsInfo({
            state: "error",
            title: i18n.t("__error"),
            info: (response.data && response.data.errMsg) || i18n.t("__fetchError"),
          });
          this.Alerted();
        }
      } catch (error) {
        this.AlertsInfo({
          state: "error",
          title: i18n.t("__error"),
          info: error.message,
        });
        this.Alerted();
      }
    },

    getStatusVariant(status) {
      const variants = {
        success: "success",
        failed: "danger",
        pending: "primary",
        timeout: "dark",
        rate_limit: "dark",
      };
      if (status && status.indexOf("skip") === 0) {
        return "warning";
      }
      return variants[status] || "secondary";
    },

    formatDateTime(datetime) {
      if (!datetime) return "";
      return moment(datetime).format("YYYY-MM-DD HH:mm:ss");
    },

    formatCell(value) {
      if (value === null || value === undefined) return "-";
      if (typeof value === "object") return JSON.stringify(value);
      return value;
    },

    prettyJson(value) {
      if (value === null || value === undefined) return "";
      if (typeof value === "string") {
        try {
          return JSON.stringify(JSON.parse(value), null, 2);
        } catch (e) {
          return value;
        }
      }
      return JSON.stringify(value, null, 2);
    },

    setDefaultDateRange() {
      this.filters.date_to = moment().format("YYYY-MM-DD");
      this.filters.date_from = moment().subtract(6, "days").format("YYYY-MM-DD");
    },

    initBootstrapTooltip() {
      this.$nextTick(function () {
        this.bootstrap_tooltip = [].slice
          .call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
          .map(function (tooltipTriggerEl) {
            return Tooltip.getOrCreateInstance(tooltipTriggerEl);
          });
      });
    },
  },
  watch: {
    logs: {
      handler() {
        this.initBootstrapTooltip();
      },
      deep: true,
    },
  },
  mounted() {
    this.setDefaultDateRange();
    this.fetchOptions();
    this.getAgencies();
    this.fetchLogs();
    this.initBootstrapTooltip();
  },
};
</script>

<style lang="scss" scoped>
pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  background-color: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
  max-height: 320px;
  overflow: auto;
}
</style>
